/* eslint-disable consistent-return */
import { Button, Chip, Grid, IconButton, MenuItem } from '@material-ui/core';
import { Add, Check, CheckRounded } from '@material-ui/icons';
import DescriptionIcon from '@material-ui/icons/Description';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import {
  createBusinessAffiliate,
  getContractTypes,
  getJobs,
  getTeamById,
  postAffilationFiles,
} from '@webapps/business';
import {
  FormikAutocompletePx,
  FormikTextField,
  Loader,
  TextField,
  useAuth,
  useMutation,
  useQuery,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import * as Yup from 'yup';

const AffiliationSendBusiness = ({
  applicant,
  offer,
  onChangeStepAffiliation,
  onClose,
  refetch,
}) => {
  const [data, setData] = useState({
    student: applicant?.id,
    team: '',
    job: '',
    contractType: offer?.contractType?.id,
    requiredFiles: [],
    additionalFiles: [],
  });
  const [currentStep, setCurrentStep] = useState(0);

  const {
    error,
    isLoading: isLoadingCreateBusinessAffiliate,
    isSuccess: isSuccessCreateAffiliation,
    mutateAsync: mutateCreateBusinessAffiliate,
    data: dataAffilation = {},
  } = useMutation(createBusinessAffiliate);

  const { addToast } = useToast();

  useEffect(() => {
    if (error) {
      addToast({
        status: 'error',
        message: "Erreur pour créer l'affiliation",
      });
    }
    if (isSuccessCreateAffiliation) {
      addToast({
        status: 'success',
        message: 'Affiliation créée avec succès',
      });
    }
    refetch();
  }, [error, isSuccessCreateAffiliation]);

  const makeRequest = async ({ team: teamRoom, job, ...values }) => {
    await mutateCreateBusinessAffiliate({
      data: {
        team: teamRoom?.id,
        job: job?.ogr,
        ...values,
      },
    });
  };

  const handleNextStep = (newData, final = false, goToLoadFile = false) => {
    setData((prev) => ({ ...prev, ...newData }));
    onChangeStepAffiliation(2);

    if (final) {
      makeRequest(newData);
      // return;
    }
    if (!goToLoadFile) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (isSuccessCreateAffiliation) {
      if (dataAffilation?.data?.additionalFiles.length === 0) {
        onClose();
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  }, [isSuccessCreateAffiliation]);

  const handlePrevStep = (newData) => {
    setData((prev) => ({ ...prev, ...newData }));
    setCurrentStep((prev) => prev - 1);
    onChangeStepAffiliation(1);
  };

  const steps = [
    <StepOne
      key={1}
      applicant={applicant}
      data={data}
      next={handleNextStep}
      offer={offer}
    />,
    <StepTwo
      key={4}
      data={data}
      isLoading={isLoadingCreateBusinessAffiliate}
      next={handleNextStep}
      prev={handlePrevStep}
    />,
    <StepThree
      key={3}
      close={onClose}
      data={dataAffilation?.data?.additionalFiles}
      isLoading={isLoadingCreateBusinessAffiliate}
      next={handleNextStep}
      prev={handlePrevStep}
    />,
  ];

  return steps[currentStep];
};

AffiliationSendBusiness.propTypes = {
  onClose: PropTypes.func,
  onChangeStepAffiliation: PropTypes.func,
  applicant: PropTypes.object,
  offer: PropTypes.object.required,
};

const stepOneValidationSchema = Yup.object({
  student: Yup?.string().required('Requis'),
  job: Yup?.object().shape({
    ogr: Yup?.string().required('Requis'),
  }),
  contractType: Yup?.string().required('Requis'),
  team: Yup?.object().shape({
    id: Yup?.string().required('Requis'),
  }),
});

const StepOne = ({ applicant, data, next }) => {
  const {
    css,
    theme: { spacing },
  } = useStyles();

  const { idUser } = useAuth();

  const { data: { content: teams = [] } = {}, isLoading: teamLoading } =
    useQuery(getTeamById, {
      key: 'team',
      variables: { id: idUser },
    });

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepOneValidationSchema,
    onSubmit: (values) => next(values),
  });

  const [keyword, setKeyword] = useState('');

  const { data: { content: jobs = [] } = {} } = useQuery(getJobs, {
    key: ['jobs', keyword],
    variables: { keyword },
    keepPreviousData: true,
    staleTime: 5000,
  });

  const { data: contractTypes = [] } = useQuery(getContractTypes, {
    key: 'contractTypes',
  });

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width: 639px)': {
          margin: spacing(1),
          marginTop: spacing(2),
        },
      })}
    >
      <Grid container>
        <Grid className={css({ marginTop: spacing(0) })} container>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Nom et prénom"
              value={`${applicant?.firstname} ${applicant?.lastname}`}
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <TextField
              disabled
              formik={formik}
              fullWidth
              label="Ville de l'apprenant"
              value={applicant?.address?.label}
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikAutocompletePx
              defaultValue=""
              formik={formik}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => value.ogr === option.ogr}
              label="Indiquer un intitulé de poste"
              name="job.ogr"
              onInputChange={(event, value) => {
                setKeyword(value);
              }}
              options={jobs}
              pathValue="ogr"
              value={
                jobs.find(({ ogr }) => ogr === formik.values.job.ogr) || null
              }
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              fullWidth
              label="Type de contrat"
              name="contractType"
              select
            >
              {contractTypes.map(({ id, label }, index) => (
                <MenuItem
                  key={index}
                  value={id}
                >
                  {label}
                </MenuItem>
              ))}
            </FormikTextField>
            {/* <TextField
              disabled
              formik={formik}
              fullWidth
              label="Type de contrat"
              value={offer?.contractType?.label}
            /> */}
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikAutocompletePx
              formik={formik}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => value.id === option.id}
              label="Sélectionner une équipe existante"
              loading={teamLoading}
              loadingText={
                <>
                  <FormattedMessage id="loading" />
                  ...
                </>
              }
              name="team.id"
              options={teams}
              pathValue="id"
              value={
                teams.find(({ id }) => id === formik.values.team.id) || null
              }
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({ textAlign: 'center', marginTop: spacing(4) })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            size="large"
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepOne.propTypes = {
  data: PropTypes.object,
  applicant: PropTypes.object,
  offer: PropTypes.object,
  next: PropTypes.func,
};

const stepTwoValidationSchema = Yup.object({
  additionalFiles: Yup.array(),
  requiredFiles: Yup.array().required('Requis'),
});

const StepTwo = ({ data, isLoading, next, prev }) => {
  const {
    css,
    theme: { spacing },
  } = useStyles();

  const [fileNameAdditional, setFileNameAdditional] = useState('');
  const [fileNameRequired, setFileNameRequired] = useState('');

  const [listRequiredFiles, setListRequiredFiles] = useState([]);
  const [listAdditionalFiles, setListAdditionalFiles] = useState([]);

  const [hideAdditionalFiles, setHideAdditionalFiles] = useState(false);
  const [hideRequiredFiles, setHideRequiredFiles] = useState(false);

  const addFileToListRequiredFile = () => {
    if (fileNameRequired) {
      setListRequiredFiles([...listRequiredFiles, { label: fileNameRequired }]);
    }
    setFileNameRequired('');
    setHideRequiredFiles(!hideRequiredFiles);
  };

  const addFileToAdditionalFile = () => {
    if (fileNameAdditional) {
      setListAdditionalFiles([
        ...listAdditionalFiles,
        { label: fileNameAdditional },
      ]);
    }
    setFileNameAdditional('');
    setHideAdditionalFiles(!hideAdditionalFiles);
  };

  const handleDeleteRequiredFile = (labelToDelete) => {
    const newList = listRequiredFiles.filter(
      ({ label }) => label !== labelToDelete
    );
    setListRequiredFiles(newList);
  };

  const handleDeleteAdditionalFile = (labelToDelete) => {
    const newList = listAdditionalFiles.filter(
      ({ label }) => label !== labelToDelete
    );
    setListAdditionalFiles(newList);
  };

  const handleOpenAddRequiredFile = () => {
    setHideRequiredFiles(!hideRequiredFiles);
  };

  const handleOpenAddAdditionalFile = () => {
    setHideAdditionalFiles(!hideAdditionalFiles);
  };

  useEffect(() => {
    formik.setFieldValue('requiredFiles', listRequiredFiles);
  }, [listRequiredFiles]);

  useEffect(() => {
    formik.setFieldValue('additionalFiles', listAdditionalFiles);
  }, [listAdditionalFiles]);

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepTwoValidationSchema,
    onSubmit: (values) => {
      next(values, true, true);
    },
  });

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width: 639px)': {
          margin: spacing(1),
          marginTop: spacing(2),
        },
      })}
    >
      <Grid container xs={12} sm={10}>
        <Grid
          className={css({
            display: 'flex',
            alignItems: 'center',
          })}
          item
          xs={12}
        >
          <PictureAsPdfIcon color="primary" />
          <div className={css({ marginLeft: spacing(1), color: 'black' })}>
            Sélectionner des documents à recevoir
          </div>
        </Grid>
        <Grid
          className={css({ display: 'flex', marginTop: spacing(1) })}
          item
          xs={12}
        >
          {listRequiredFiles.length <= 0 && <div>Aucun document indiqué</div>}
        </Grid>
        <Grid className={css({ display: 'flex' })} item xs={12}>
          <div>
            {listRequiredFiles.length > 0 &&
              listRequiredFiles.map(({ label }, index) => (
                <Chip
                  key={index}
                  className={css({ margin: spacing(0.5) })}
                  color="primary"
                  label={label}
                  onDelete={() => handleDeleteRequiredFile(label)}
                />
              ))}
          </div>
        </Grid>
        {!hideRequiredFiles ? (
          <Grid
            className={css({ display: 'flex', marginTop: spacing(1) })}
            item
            xs={12}
          >
            <IconButton
              className={css({
                background: '#d5e2fe',
              })}
              color="primary"
              onClick={handleOpenAddRequiredFile}
              size="small"
              variant="outlined"
            >
              <Add />
            </IconButton>
          </Grid>
        ) : (
          <Grid
            className={css({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: spacing(1),
            })}
            item
            xs={12}
          >
            <div className={css({ width: '80%' })}>
              <TextField
                className={css({ width: 30 })}
                fullWidth
                label="Nom du fichier"
                onChange={(event) => {
                  setFileNameRequired(event.target.value);
                }}
                value={fileNameRequired}
              />
            </div>
            <div>
              <IconButton
                className={css({ background: '#d5e2fe' })}
                color="primary"
                onClick={addFileToListRequiredFile}
                variant="outlined"
              >
                <CheckRounded />
              </IconButton>
            </div>
          </Grid>
        )}
        <Grid
          className={css({
            display: 'flex',
            alignItems: 'center',
            marginTop: spacing(3),
          })}
          item
          xs={12}
        >
          <DescriptionIcon color="primary" />
          <div
            className={css({
              marginLeft: spacing(1),
              color: 'black',
            })}
          >
            Envoyer des documents (PDF uniquement)
          </div>
        </Grid>
        <Grid
          className={css({ display: 'flex', marginTop: spacing(1) })}
          item
          xs={12}
        >
          {listAdditionalFiles.length <= 0 && <div>Aucun document indiqué</div>}
        </Grid>
        <Grid className={css({ display: 'flex' })} item xs={12}>
          <div>
            {listAdditionalFiles.length > 0 &&
              listAdditionalFiles.map(({ label }, index) => (
                <Chip
                  key={index}
                  className={css({ margin: spacing(0.5) })}
                  color="primary"
                  label={label}
                  onDelete={() => handleDeleteAdditionalFile(label)}
                />
              ))}
          </div>
        </Grid>
        {!hideAdditionalFiles ? (
          <Grid
            className={css({ display: 'flex', marginTop: spacing(1) })}
            item
            xs={12}
          >
            <IconButton
              className={css({
                background: '#d5e2fe',
              })}
              color="primary"
              onClick={handleOpenAddAdditionalFile}
              size="small"
              variant="outlined"
            >
              <Add />
            </IconButton>
          </Grid>
        ) : (
          <Grid
            className={css({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: spacing(1),
            })}
            item
            xs={12}
          >
            <div className={css({ width: '80%' })}>
              <TextField
                className={css({ width: 30 })}
                fullWidth
                label="Nom du fichier"
                onChange={(event) => {
                  setFileNameAdditional(event.target.value);
                }}
                value={fileNameAdditional}
              />
            </div>
            <div>
              <IconButton
                className={css({ background: '#d5e2fe' })}
                color="primary"
                onClick={addFileToAdditionalFile}
                variant="outlined"
              >
                <CheckRounded />
              </IconButton>
            </div>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20, backgroundColor: 'white' })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            size="large"
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            disabled={isLoading}
            fullWidth
            onClick={formik.handleSubmit}
            size="large"
            type="submit"
            variant="contained"
          >
            Envoyer
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepTwo.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
  isLoading: PropTypes.bool,
};

const StepThree = ({ close, data, isLoading }) => {
  const {
    css,
    theme: { spacing },
  } = useStyles();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [files, setFiles] = useState([]);

  const {
    isLoading: isLoadingAffiliationFile,
    isSuccess,
    mutateAsync: mutateAffiliationFiles,
  } = useMutation(postAffilationFiles);

  const addFiles = async () => {
    await mutateAffiliationFiles({
      data: {
        params: data?.map(({ id }) => id),
        files,
      },
    });
  };

  const handleAddFiles = (file, index) => {
    files[index] = file;
  };

  const { addToast } = useToast();

  useEffect(() => {
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'Fichier(s) envoyé(s) avec succès',
      });
      close();
    }
  }, [isSuccess]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
      })}
    >
      <Grid container xs={10}>
        <Grid
          className={css({
            display: 'flex',
            alignItems: 'center',
            marginTop: spacing(0),
          })}
          item
          xs={12}
        >
          <PictureAsPdfIcon color="primary" />
          <div className={css({ marginLeft: spacing(1), color: 'black' })}>
            Envoyer des documents (PDF uniquement)
          </div>
        </Grid>
        <Grid
          className={css({ display: 'flex', marginTop: spacing(1) })}
          item
          xs={12}
        >
          {data?.length <= 0 && <div>Aucun document indiqué</div>}
        </Grid>
        {data?.length > 0 &&
          data?.map(({ label }, index) => (
            <Grid
              key={index}
              className={css({
                display: 'flex',
                marginTop: spacing(1),
                flexDirection: 'column',
              })}
              item
              xs={12}
            >
              <div className={css({ color: 'black' })}>{label}</div>
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                })}
              >
                <div>
                  {files[index]
                    ? files[index]?.name
                    : 'Sélectionnez le fichier'}
                </div>
                <Button
                  component="label"
                  disableElevation
                  size="small"
                  variant="contained"
                >
                  Importer
                  <input
                    accept="application/pdf"
                    hidden
                    inputProps={{
                      accept: 'application/pdf',
                    }}
                    onChange={(event) => {
                      handleAddFiles(event.target.files[0], index);
                    }}
                    type="file"
                  />
                </Button>

                {/* <Input
                  inputProps={{
                    accept: 'application/pdf',
                  }}
                  onChange={(event) => {
                    handleAddFiles(event.target.files[0], index);
                  }}
                  type="file"
                /> */}
              </div>
            </Grid>
          ))}
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={close}
            size="large"
            variant="outlined"
          >
            Plus tard
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            disabled={isLoadingAffiliationFile}
            fullWidth
            onClick={addFiles}
            size="large"
            type="submit"
            variant="contained"
          >
            Envoyer
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepThree.propTypes = {
  data: PropTypes.object,
  close: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default AffiliationSendBusiness;
